import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { csrfFetch } from '~/lib/api/csrf-client';

interface Project {
  id: string;
  externalId: string;
  name: string;
  role?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface OrganizationMember {
  id: string;
  email: string;
  displayName: string;
  role: string;
  createdAt?: string;
}

type DashboardSection = 'home' | 'apps' | 'workspaces' | 'team';
type EditorMode = 'create' | 'edit';

interface EditorState {
  mode: EditorMode;
  originalExternalId?: string;
  name: string;
  externalId: string;
  prompt: string;
}

interface MemberForm {
  displayName: string;
  email: string;
  password: string;
  role: 'admin' | 'member' | 'viewer';
}

const PROJECT_STARTERS = [
  {
    label: 'CRM e vendas',
    name: 'CRM Comercial',
    prompt: 'Crie um CRM com leads, empresas, funil de vendas, tarefas e follow-ups.',
  },
  {
    label: 'Portal do cliente',
    name: 'Portal do Cliente',
    prompt: 'Crie um portal para clientes com solicitacoes, arquivos, aprovacoes e status de entrega.',
  },
  {
    label: 'Operacao interna',
    name: 'Painel Operacional',
    prompt: 'Crie um painel operacional com metricas, filas, alertas e atividade da equipe.',
  },
  {
    label: 'Financeiro',
    name: 'Gestao Financeira',
    prompt: 'Crie um app financeiro com contas, recebimentos, despesas, fluxo de caixa e relatorios.',
  },
] as const;

const NAV_ITEMS: Array<{ id: DashboardSection; label: string; icon: string }> = [
  { id: 'home', label: 'Inicio', icon: 'i-ph:house' },
  { id: 'apps', label: 'Apps', icon: 'i-ph:grid-four' },
  { id: 'workspaces', label: 'Workspaces', icon: 'i-ph:buildings' },
  { id: 'team', label: 'Time', icon: 'i-ph:users-three' },
];

function slugifyProjectName(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'project'
  );
}

function initialsFromLabel(label: string): string {
  return (
    label
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'W'
  );
}

function formatDate(value?: string): string {
  if (!value) {
    return 'Sem data';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}

export function OrganizationProjects() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<DashboardSection>('home');
  const [projects, setProjects] = useState<Project[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [activeOrganizationId, setActiveOrganizationId] = useState<string | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingWorkspace, setLoadingWorkspace] = useState(true);
  const [saving, setSaving] = useState(false);
  const [switchingOrganizationId, setSwitchingOrganizationId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [creatingMember, setCreatingMember] = useState(false);
  const [error, setError] = useState('');
  const [workspaceError, setWorkspaceError] = useState('');
  const [memberError, setMemberError] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<'updated' | 'name'>('updated');
  const [editor, setEditor] = useState<EditorState>({
    mode: 'create',
    name: '',
    externalId: '',
    prompt: '',
  });
  const [memberForm, setMemberForm] = useState<MemberForm>({
    displayName: '',
    email: '',
    password: '',
    role: 'member',
  });

  const activeOrganization = useMemo(
    () => organizations.find((organization) => organization.id === activeOrganizationId) ?? organizations[0],
    [activeOrganizationId, organizations],
  );
  const canSave =
    editor.name.trim().length > 0 &&
    /^[\w-]+$/.test(editor.externalId) &&
    (editor.mode === 'edit' || editor.prompt.trim().length > 0);
  const canManageTeam = activeOrganization?.role === 'owner' || activeOrganization?.role === 'admin';

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError('');

    try {
      const response = await fetch('/api/projects', { credentials: 'same-origin' });
      const body = (await response.json()) as { data?: { projects?: Project[] }; error?: { message?: string } };

      if (!response.ok) {
        throw new Error(body.error?.message || 'Unable to load apps');
      }

      setProjects(body.data?.projects ?? []);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load apps');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadWorkspaceData = useCallback(async () => {
    setLoadingWorkspace(true);
    setWorkspaceError('');
    setMemberError('');

    try {
      const [organizationsResponse, membersResponse] = await Promise.all([
        fetch('/api/organizations', { credentials: 'same-origin' }),
        fetch('/api/organization-members', { credentials: 'same-origin' }),
      ]);
      const organizationsBody = (await organizationsResponse.json()) as {
        data?: { activeOrganizationId?: string; organizations?: Organization[] };
        error?: { message?: string };
      };
      const membersBody = (await membersResponse.json()) as {
        data?: { members?: OrganizationMember[] };
        error?: { message?: string };
      };

      if (!organizationsResponse.ok) {
        throw new Error(organizationsBody.error?.message || 'Unable to load workspaces');
      }

      setOrganizations(organizationsBody.data?.organizations ?? []);
      setActiveOrganizationId(organizationsBody.data?.activeOrganizationId ?? null);

      if (!membersResponse.ok) {
        setMemberError(membersBody.error?.message || 'Unable to load members');
      } else {
        setMembers(membersBody.data?.members ?? []);
      }
    } catch (loadError) {
      setWorkspaceError(loadError instanceof Error ? loadError.message : 'Unable to load workspace');
    } finally {
      setLoadingWorkspace(false);
    }
  }, []);

  useEffect(() => {
    void Promise.all([loadProjects(), loadWorkspaceData()]);
  }, [loadProjects, loadWorkspaceData]);

  const filteredProjects = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return projects
      .filter((project) => !normalized || `${project.name} ${project.externalId}`.toLowerCase().includes(normalized))
      .sort((a, b) => {
        if (sort === 'name') {
          return a.name.localeCompare(b.name);
        }

        return (b.updatedAt ?? b.createdAt ?? '').localeCompare(a.updatedAt ?? a.createdAt ?? '');
      });
  }, [projects, query, sort]);

  const recentProjects = useMemo(() => filteredProjects.slice(0, 4), [filteredProjects]);

  const resetEditor = () => {
    setEditor({ mode: 'create', name: '', externalId: '', prompt: '' });
    setError('');
  };

  const saveProject = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (!canSave) {
      setError('Use nome e identificador validos para o app.');
      return;
    }

    setSaving(true);
    setError('');

    const isEdit = editor.mode === 'edit';
    const externalId = editor.externalId.trim();

    try {
      const response = await csrfFetch('/api/projects', {
        method: isEdit ? 'PUT' : 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          isEdit
            ? {
                projectId: editor.originalExternalId,
                name: editor.name.trim(),
                externalId,
              }
            : {
                name: editor.name.trim(),
                externalId,
              },
        ),
      });
      const body = (await response.json()) as { error?: { message?: string } };

      if (!response.ok) {
        throw new Error(body.error?.message || 'Unable to save app');
      }

      resetEditor();
      await loadProjects();

      if (!isEdit) {
        navigate(`/chat/${encodeURIComponent(externalId)}`);
      }
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save app');
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (project: Project) => {
    if (!window.confirm(`Delete "${project.name}"?`)) {
      return;
    }

    setDeletingId(project.externalId);
    setError('');

    try {
      const response = await csrfFetch('/api/projects', {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: project.externalId }),
      });
      const body = (await response.json()) as { error?: { message?: string } };

      if (!response.ok) {
        throw new Error(body.error?.message || 'Unable to delete app');
      }

      await loadProjects();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Unable to delete app');
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (project: Project) => {
    setEditor({
      mode: 'edit',
      originalExternalId: project.externalId,
      name: project.name,
      externalId: project.externalId,
      prompt: '',
    });
    setActiveSection('home');
    setError('');
  };

  const useStarter = (starter: (typeof PROJECT_STARTERS)[number]) => {
    setEditor({
      mode: 'create',
      name: starter.name,
      externalId: slugifyProjectName(starter.name),
      prompt: starter.prompt,
    });
    setActiveSection('home');
    setError('');
  };

  const switchOrganization = async (organizationId: string) => {
    if (organizationId === activeOrganizationId) {
      return;
    }

    setSwitchingOrganizationId(organizationId);
    setWorkspaceError('');

    try {
      const response = await csrfFetch('/api/organizations', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId }),
      });
      const body = (await response.json()) as { error?: { message?: string } };

      if (!response.ok) {
        throw new Error(body.error?.message || 'Unable to switch workspace');
      }

      await Promise.all([loadProjects(), loadWorkspaceData()]);
    } catch (switchError) {
      setWorkspaceError(switchError instanceof Error ? switchError.message : 'Unable to switch workspace');
    } finally {
      setSwitchingOrganizationId(null);
    }
  };

  const createMember = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreatingMember(true);
    setMemberError('');

    try {
      const response = await csrfFetch('/api/organization-members', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          displayName: memberForm.displayName.trim(),
          email: memberForm.email.trim(),
          password: memberForm.password,
          role: memberForm.role,
        }),
      });
      const body = (await response.json()) as { error?: { message?: string } };

      if (!response.ok) {
        throw new Error(body.error?.message || 'Unable to create member');
      }

      setMemberForm({ displayName: '', email: '', password: '', role: 'member' });
      await loadWorkspaceData();
    } catch (createError) {
      setMemberError(createError instanceof Error ? createError.message : 'Unable to create member');
    } finally {
      setCreatingMember(false);
    }
  };

  const updatePrompt = (prompt: string) => {
    setEditor((current) => {
      const title =
        prompt
          .split(/[.,\n]/)[0]
          ?.trim()
          .slice(0, 60) || '';

      return {
        ...current,
        prompt,
        name: current.name || title,
        externalId: current.externalId || slugifyProjectName(title),
      };
    });
  };

  return (
    <main id="main-content" className="min-h-full overflow-auto bg-[#f3f2ef] text-[#111113]">
      <div className="mx-auto grid min-h-full w-full max-w-[1500px] gap-4 px-4 py-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="flex min-h-[calc(100vh-2rem)] flex-col rounded-2xl border border-black/10 bg-white p-3 shadow-[0_1px_2px_rgba(17,17,20,0.04),0_14px_42px_rgba(17,17,20,0.08)]">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#111113] text-white">
              <span className="i-ph:sparkle h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">Excalibur Creator</p>
              <p className="truncate text-xs text-[#586070]">AI app builder</p>
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-black/10 bg-[#f8f7f4] p-2">
            <button
              type="button"
              onClick={() => setActiveSection('workspaces')}
              className="flex w-full cursor-pointer items-center gap-3 rounded-lg bg-transparent px-2 py-2 text-left transition-colors hover:bg-white"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-sm font-semibold text-[#111113] shadow-sm">
                {initialsFromLabel(activeOrganization?.name ?? 'Workspace')}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{activeOrganization?.name ?? 'Workspace'}</span>
                <span className="block truncate text-xs text-[#586070]">{projects.length} apps neste espaco</span>
              </span>
              <span className="i-ph:caret-down h-4 w-4 text-[#8b92a1]" />
            </button>
          </div>

          <nav className="mt-4 grid gap-1" aria-label="Navegacao principal">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-current={activeSection === item.id ? 'page' : undefined}
                onClick={() => setActiveSection(item.id)}
                className={`flex h-10 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors ${
                  activeSection === item.id
                    ? 'bg-[#111113] text-white'
                    : 'bg-transparent text-[#586070] hover:bg-[#f8f7f4] hover:text-[#111113]'
                }`}
              >
                <span className={`${item.icon} h-5 w-5`} />
                {item.label}
              </button>
            ))}
          </nav>

          <section className="mt-5 min-h-0 flex-1">
            <div className="flex items-center justify-between px-3 text-xs font-semibold uppercase text-[#8b92a1]">
              <span>Recentes</span>
              <span>{recentProjects.length}</span>
            </div>
            <div className="mt-2 grid gap-1">
              {recentProjects.length === 0 ? (
                <p className="rounded-lg px-3 py-2 text-xs text-[#8b92a1]">Sem apps recentes.</p>
              ) : (
                recentProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/chat/${encodeURIComponent(project.externalId)}`}
                    className="flex min-h-10 items-center gap-2 rounded-lg px-3 py-2 text-sm text-[#586070] transition-colors hover:bg-[#f8f7f4] hover:text-[#111113]"
                  >
                    <span className="i-ph:app-window h-4 w-4 shrink-0" />
                    <span className="truncate">{project.name}</span>
                  </Link>
                ))
              )}
            </div>
          </section>

          <button
            type="button"
            onClick={() => {
              resetEditor();
              setActiveSection('home');
            }}
            className="mt-4 inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#111113] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2b2d33] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="i-ph:plus h-4 w-4" />
            Novo app
          </button>
        </aside>

        <section className="min-w-0">
          <header className="mb-4 flex min-h-14 flex-col gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(17,17,20,0.04)] md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase text-[#8b92a1]">
                {activeOrganization?.name ?? 'Workspace'}
              </p>
              <h1 className="truncate text-xl font-semibold text-[#111113]">
                {activeSection === 'home' && 'Inicio'}
                {activeSection === 'apps' && 'Apps'}
                {activeSection === 'workspaces' && 'Workspaces'}
                {activeSection === 'team' && 'Time e acesso'}
              </h1>
            </div>
            <label className="flex h-10 w-full max-w-md items-center gap-2 rounded-xl border border-black/10 bg-[#f8f7f4] px-3 text-sm text-[#586070] focus-within:border-[#111113] md:w-80">
              <span className="i-ph:magnifying-glass h-4 w-4" />
              <input
                aria-label="Pesquisar apps"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Pesquisar apps"
                className="min-w-0 flex-1 bg-transparent text-[#111113] outline-none placeholder:text-[#8b92a1]"
              />
            </label>
          </header>

          {activeSection === 'home' && (
            <HomeSection
              canSave={canSave}
              editor={editor}
              error={error}
              loadingProjects={loadingProjects}
              projects={recentProjects}
              saving={saving}
              workspaceName={activeOrganization?.name ?? 'Workspace'}
              onCancelEdit={resetEditor}
              onOpenApps={() => setActiveSection('apps')}
              onSaveProject={saveProject}
              onUpdateEditor={setEditor}
              onUpdatePrompt={updatePrompt}
              onUseStarter={useStarter}
            />
          )}

          {activeSection === 'apps' && (
            <AppsSection
              deletingId={deletingId}
              error={error}
              loading={loadingProjects}
              projects={filteredProjects}
              sort={sort}
              workspaceName={activeOrganization?.name ?? 'Workspace'}
              onDeleteProject={deleteProject}
              onEditProject={startEdit}
              onNewProject={() => {
                resetEditor();
                setActiveSection('home');
              }}
              onSortChange={setSort}
            />
          )}

          {activeSection === 'workspaces' && (
            <WorkspacesSection
              activeOrganizationId={activeOrganizationId}
              loading={loadingWorkspace}
              organizations={organizations}
              projectCount={projects.length}
              switchingOrganizationId={switchingOrganizationId}
              workspaceError={workspaceError}
              onSwitchOrganization={switchOrganization}
            />
          )}

          {activeSection === 'team' && (
            <TeamSection
              canManageTeam={canManageTeam}
              creatingMember={creatingMember}
              memberError={memberError}
              memberForm={memberForm}
              members={members}
              workspaceName={activeOrganization?.name ?? 'Workspace'}
              onCreateMember={createMember}
              onMemberFormChange={setMemberForm}
            />
          )}
        </section>
      </div>
    </main>
  );
}

function HomeSection({
  canSave,
  editor,
  error,
  loadingProjects,
  projects,
  saving,
  workspaceName,
  onCancelEdit,
  onOpenApps,
  onSaveProject,
  onUpdateEditor,
  onUpdatePrompt,
  onUseStarter,
}: {
  canSave: boolean;
  editor: EditorState;
  error: string;
  loadingProjects: boolean;
  projects: Project[];
  saving: boolean;
  workspaceName: string;
  onCancelEdit: () => void;
  onOpenApps: () => void;
  onSaveProject: (event: FormEvent<HTMLFormElement>) => void;
  onUpdateEditor: React.Dispatch<React.SetStateAction<EditorState>>;
  onUpdatePrompt: (prompt: string) => void;
  onUseStarter: (starter: (typeof PROJECT_STARTERS)[number]) => void;
}) {
  return (
    <div className="grid gap-4">
      <section className="rounded-2xl border border-black/10 bg-white p-5 shadow-[0_1px_2px_rgba(17,17,20,0.04),0_14px_42px_rgba(17,17,20,0.08)] md:p-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-semibold uppercase text-[#8b92a1]">{workspaceName}</p>
          <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#111113] md:text-5xl">
            O que voce vai construir em seguida?
          </h2>
        </div>

        <form onSubmit={onSaveProject} className="mx-auto mt-7 max-w-4xl">
          <div className="rounded-2xl border border-black/10 bg-[#f8f7f4] p-3 transition-colors focus-within:border-[#111113] focus-within:bg-white">
            <textarea
              value={editor.prompt}
              onChange={(event) => onUpdatePrompt(event.target.value)}
              placeholder="Descreva o aplicativo que voce quer criar..."
              rows={4}
              disabled={editor.mode === 'edit'}
              aria-label="Descreva o app que deseja criar"
              className="min-h-32 w-full resize-none bg-transparent px-2 py-2 text-base text-[#111113] outline-none placeholder:text-[#8b92a1] disabled:cursor-not-allowed disabled:opacity-60"
            />

            <div className="grid gap-3 border-t border-black/10 pt-3 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
              <label className="grid gap-1 text-xs font-medium text-[#586070]">
                Nome do app
                <input
                  value={editor.name}
                  onChange={(event) => {
                    const name = event.target.value;
                    onUpdateEditor((current) => ({
                      ...current,
                      name,
                      externalId:
                        current.mode === 'create' &&
                        (!current.externalId || current.externalId === slugifyProjectName(current.name))
                          ? slugifyProjectName(name)
                          : current.externalId,
                    }));
                  }}
                  required
                  className="h-11 rounded-xl border border-black/10 bg-white px-3 text-sm text-[#111113] outline-none focus:border-[#111113]"
                />
              </label>

              <label className="grid gap-1 text-xs font-medium text-[#586070]">
                Identificador
                <input
                  value={editor.externalId}
                  onChange={(event) => onUpdateEditor((current) => ({ ...current, externalId: event.target.value }))}
                  required
                  pattern="[\w-]+"
                  className="h-11 rounded-xl border border-black/10 bg-white px-3 font-mono text-sm text-[#111113] outline-none focus:border-[#111113]"
                />
              </label>

              <button
                type="submit"
                disabled={saving || !canSave}
                className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#111113] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#2b2d33] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className={saving ? 'i-svg-spinners:90-ring-with-bg h-4 w-4' : 'i-ph:arrow-right h-4 w-4'} />
                {saving ? 'Salvando' : editor.mode === 'edit' ? 'Salvar' : 'Criar app'}
              </button>
            </div>
          </div>

          {error && (
            <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
          )}

          {editor.mode === 'edit' && (
            <button
              type="button"
              onClick={onCancelEdit}
              className="mt-3 h-10 rounded-xl border border-black/10 bg-white px-4 text-sm font-medium text-[#586070] transition-colors hover:text-[#111113]"
            >
              Cancelar edicao
            </button>
          )}
        </form>

        <div className="mx-auto mt-4 flex max-w-4xl flex-wrap justify-center gap-2">
          {PROJECT_STARTERS.map((starter) => (
            <button
              key={starter.label}
              type="button"
              onClick={() => onUseStarter(starter)}
              className="h-9 rounded-full border border-black/10 bg-white px-3 text-sm font-medium text-[#586070] transition-colors hover:border-[#111113] hover:text-[#111113]"
            >
              {starter.label}
            </button>
          ))}
          <button
            type="button"
            onClick={onOpenApps}
            className="h-9 rounded-full border border-black/10 bg-white px-3 text-sm font-medium text-[#586070] transition-colors hover:border-[#111113] hover:text-[#111113]"
          >
            Mais
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-black/10 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Apps recentes</h2>
            <p className="text-sm text-[#586070]">Continue a partir dos ultimos projetos.</p>
          </div>
          <button
            type="button"
            onClick={onOpenApps}
            className="rounded-lg bg-transparent px-2 py-1 text-sm font-semibold text-[#111113] hover:bg-[#f8f7f4]"
          >
            Ver apps
          </button>
        </div>
        <ProjectList loading={loadingProjects} projects={projects} compact />
      </section>
    </div>
  );
}

function AppsSection({
  deletingId,
  error,
  loading,
  projects,
  sort,
  workspaceName,
  onDeleteProject,
  onEditProject,
  onNewProject,
  onSortChange,
}: {
  deletingId: string | null;
  error: string;
  loading: boolean;
  projects: Project[];
  sort: 'updated' | 'name';
  workspaceName: string;
  onDeleteProject: (project: Project) => void;
  onEditProject: (project: Project) => void;
  onNewProject: () => void;
  onSortChange: (sort: 'updated' | 'name') => void;
}) {
  return (
    <section className="grid gap-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-black/10 bg-white p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase text-[#8b92a1]">{workspaceName}</p>
          <h2 className="text-lg font-semibold">Apps</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            aria-label="Ordenacao"
            value={sort}
            onChange={(event) => onSortChange(event.target.value as 'updated' | 'name')}
            className="h-10 rounded-xl border border-black/10 bg-white px-3 text-sm outline-none focus:border-[#111113]"
          >
            <option value="updated">Ultima atualizacao</option>
            <option value="name">Nome</option>
          </select>
          <button
            type="button"
            onClick={onNewProject}
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl bg-[#111113] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2b2d33]"
          >
            <span className="i-ph:plus h-4 w-4" />
            Criar app
          </button>
        </div>
      </div>

      {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
      <ProjectList
        deletingId={deletingId}
        loading={loading}
        projects={projects}
        onDeleteProject={onDeleteProject}
        onEditProject={onEditProject}
      />
    </section>
  );
}

function ProjectList({
  compact = false,
  deletingId,
  loading,
  projects,
  onDeleteProject,
  onEditProject,
}: {
  compact?: boolean;
  deletingId?: string | null;
  loading: boolean;
  projects: Project[];
  onDeleteProject?: (project: Project) => void;
  onEditProject?: (project: Project) => void;
}) {
  if (loading) {
    return (
      <div className="grid min-h-44 place-items-center rounded-2xl border border-black/10 bg-[#f8f7f4]">
        <div className="i-svg-spinners:90-ring-with-bg h-6 w-6 text-[#111113]" aria-label="Carregando apps" />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="grid min-h-44 place-items-center rounded-2xl border border-dashed border-black/20 bg-[#f8f7f4] px-4 text-center text-sm text-[#586070]">
        Nenhum app encontrado neste workspace.
      </div>
    );
  }

  return (
    <div className={`grid gap-3 ${compact ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-2 xl:grid-cols-3'}`}>
      {projects.map((project) => (
        <article
          key={project.id}
          className="rounded-2xl border border-black/10 bg-white p-4 transition-shadow hover:shadow-[0_14px_42px_rgba(17,17,20,0.08)]"
        >
          <Link to={`/chat/${encodeURIComponent(project.externalId)}`} className="block">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#f8f7f4] text-[#111113]">
              <span className="i-ph:app-window h-7 w-7" />
            </span>
            <span className="mt-4 block min-w-0">
              <strong className="block truncate text-base font-semibold text-[#111113]">{project.name}</strong>
              <small className="mt-1 block truncate font-mono text-xs text-[#586070]">/chat/{project.externalId}</small>
              <small className="mt-3 block text-xs text-[#8b92a1]">
                Atualizado em {formatDate(project.updatedAt ?? project.createdAt)}
              </small>
            </span>
          </Link>

          {!compact && (
            <div className="mt-4 flex items-center gap-2">
              <Link
                to={`/chat/${encodeURIComponent(project.externalId)}`}
                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-[#111113] px-3 text-sm font-semibold text-white transition-colors hover:bg-[#2b2d33]"
              >
                Abrir
                <span className="i-ph:arrow-right h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={() => onEditProject?.(project)}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-black/10 bg-white text-[#586070] transition-colors hover:text-[#111113]"
                aria-label={`Editar ${project.name}`}
                title="Editar"
              >
                <span className="i-ph:pencil-simple h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => onDeleteProject?.(project)}
                disabled={deletingId === project.externalId}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 bg-white text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={`Excluir ${project.name}`}
                title="Excluir"
              >
                <span
                  className={
                    deletingId === project.externalId ? 'i-svg-spinners:90-ring-with-bg h-4 w-4' : 'i-ph:trash h-4 w-4'
                  }
                />
              </button>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function WorkspacesSection({
  activeOrganizationId,
  loading,
  organizations,
  projectCount,
  switchingOrganizationId,
  workspaceError,
  onSwitchOrganization,
}: {
  activeOrganizationId: string | null;
  loading: boolean;
  organizations: Organization[];
  projectCount: number;
  switchingOrganizationId: string | null;
  workspaceError: string;
  onSwitchOrganization: (organizationId: string) => void;
}) {
  return (
    <section className="grid gap-4">
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <h2 className="text-lg font-semibold">Workspaces</h2>
        <p className="mt-1 text-sm text-[#586070]">Espacos disponiveis para este usuario.</p>
      </div>
      {workspaceError && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{workspaceError}</p>
      )}
      {loading ? (
        <div className="grid min-h-44 place-items-center rounded-2xl border border-black/10 bg-white">
          <div className="i-svg-spinners:90-ring-with-bg h-6 w-6" aria-label="Carregando workspaces" />
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {organizations.map((organization) => {
            const active = organization.id === activeOrganizationId;

            return (
              <article
                key={organization.id}
                className={`rounded-2xl border bg-white p-4 ${active ? 'border-[#111113]' : 'border-black/10'}`}
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#f8f7f4] text-sm font-semibold">
                    {initialsFromLabel(organization.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate text-base">{organization.name}</strong>
                    <small className="block truncate text-xs text-[#586070]">{organization.slug}</small>
                    <small className="mt-2 inline-flex rounded-full border border-black/10 px-2 py-1 text-xs uppercase text-[#586070]">
                      {organization.role}
                    </small>
                  </span>
                </div>
                <div className="mt-4 flex items-center justify-between rounded-xl bg-[#f8f7f4] px-3 py-2 text-sm">
                  <span className="text-[#586070]">Apps neste espaco</span>
                  <strong>{active ? projectCount : '-'}</strong>
                </div>
                <button
                  type="button"
                  onClick={() => onSwitchOrganization(organization.id)}
                  disabled={active || switchingOrganizationId === organization.id}
                  className="mt-4 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-black/10 bg-white text-sm font-semibold text-[#111113] transition-colors hover:bg-[#f8f7f4] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {switchingOrganizationId === organization.id && (
                    <span className="i-svg-spinners:90-ring-with-bg h-4 w-4" />
                  )}
                  {active ? 'Workspace ativo' : 'Trocar workspace'}
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function TeamSection({
  canManageTeam,
  creatingMember,
  memberError,
  memberForm,
  members,
  workspaceName,
  onCreateMember,
  onMemberFormChange,
}: {
  canManageTeam: boolean;
  creatingMember: boolean;
  memberError: string;
  memberForm: MemberForm;
  members: OrganizationMember[];
  workspaceName: string;
  onCreateMember: (event: FormEvent<HTMLFormElement>) => void;
  onMemberFormChange: React.Dispatch<React.SetStateAction<MemberForm>>;
}) {
  return (
    <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase text-[#8b92a1]">{workspaceName}</p>
          <h2 className="text-lg font-semibold">Membros</h2>
        </div>
        {memberError && (
          <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {memberError}
          </p>
        )}
        <div className="grid gap-2">
          {members.map((member) => (
            <div key={member.id} className="flex items-center gap-3 rounded-xl border border-black/10 px-3 py-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f8f7f4] text-sm font-semibold">
                {initialsFromLabel(member.displayName || member.email)}
              </span>
              <span className="min-w-0 flex-1">
                <strong className="block truncate text-sm">{member.displayName}</strong>
                <small className="block truncate text-xs text-[#586070]">{member.email}</small>
              </span>
              <span className="rounded-full border border-black/10 px-2 py-1 text-xs font-semibold uppercase text-[#586070]">
                {member.role}
              </span>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={onCreateMember} className="h-fit rounded-2xl border border-black/10 bg-white p-5">
        <h2 className="text-lg font-semibold">Adicionar membro</h2>
        <p className="mt-1 text-sm text-[#586070]">Cria acesso inicial para este workspace.</p>
        <fieldset disabled={!canManageTeam || creatingMember} className="mt-4 grid gap-3 disabled:opacity-60">
          <label className="grid gap-1 text-xs font-medium text-[#586070]">
            Nome
            <input
              value={memberForm.displayName}
              onChange={(event) => onMemberFormChange((current) => ({ ...current, displayName: event.target.value }))}
              required
              className="h-11 rounded-xl border border-black/10 px-3 text-sm text-[#111113] outline-none focus:border-[#111113]"
            />
          </label>
          <label className="grid gap-1 text-xs font-medium text-[#586070]">
            Email
            <input
              value={memberForm.email}
              onChange={(event) => onMemberFormChange((current) => ({ ...current, email: event.target.value }))}
              required
              type="email"
              className="h-11 rounded-xl border border-black/10 px-3 text-sm text-[#111113] outline-none focus:border-[#111113]"
            />
          </label>
          <label className="grid gap-1 text-xs font-medium text-[#586070]">
            Senha inicial
            <input
              value={memberForm.password}
              onChange={(event) => onMemberFormChange((current) => ({ ...current, password: event.target.value }))}
              required
              minLength={6}
              type="password"
              className="h-11 rounded-xl border border-black/10 px-3 text-sm text-[#111113] outline-none focus:border-[#111113]"
            />
          </label>
          <label className="grid gap-1 text-xs font-medium text-[#586070]">
            Papel
            <select
              value={memberForm.role}
              onChange={(event) =>
                onMemberFormChange((current) => ({ ...current, role: event.target.value as MemberForm['role'] }))
              }
              className="h-11 rounded-xl border border-black/10 bg-white px-3 text-sm text-[#111113] outline-none focus:border-[#111113]"
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
            </select>
          </label>
        </fieldset>
        {!canManageTeam && (
          <p className="mt-3 text-sm text-[#8b92a1]">Apenas owners e admins podem adicionar membros.</p>
        )}
        <button
          type="submit"
          disabled={!canManageTeam || creatingMember}
          className="mt-4 inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#111113] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2b2d33] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={creatingMember ? 'i-svg-spinners:90-ring-with-bg h-4 w-4' : 'i-ph:user-plus h-4 w-4'} />
          {creatingMember ? 'Criando' : 'Adicionar membro'}
        </button>
      </form>
    </section>
  );
}
